import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Cpu,
  Code2,
  ChevronDown,
  HardDrive,
  Smartphone,
  Shield,
  Wrench,
  Router,
  Network,
  Lock,
  Server,
} from "lucide-react";

const IMAGES = {
  hero: "/assets/5675771.jpg",
};

export default function ANPlus() {
  const [tab, setTab] = useState("a+");

  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 80 });
    window.scrollTo(0, 0);
  }, []);

  const commonProjects = [
    {
      title: "PC Assembly Lab",
      desc: "Hands-on experience assembling, troubleshooting and upgrading computer hardware.",
      icon: Cpu,
    },
    {
      title: "Network Setup Project",
      desc: "Configure LAN, routers, and switches for small office/home office environments.",
      icon: Network,
    },
    {
      title: "Troubleshooting Simulations",
      desc: "Practice diagnostics and fault isolation in real-world scenarios.",
      icon: Server,
    },
  ];

  const aPlusSyllabus = [
    {
      title: "PC hardware & peripherals",
      desc: "Learn components like CPU, RAM, storage, printers & peripherals.",
      icon: <Cpu className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
    {
      title: "Operating systems installation & troubleshooting",
      desc: "Install, configure and repair Windows, Linux, and macOS.",
      icon: <HardDrive className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
    {
      title: "Mobile device basics & hardware issues",
      desc: "Understand smartphones, tablets, and common device troubleshooting.",
      icon: <Smartphone className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
    {
      title: "Hardware security & safety protocols",
      desc: "Follow ESD safety, backup and secure hardware handling.",
      icon: <Shield className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
    {
      title: "Diagnostics and preventive maintenance",
      desc: "Run diagnostic tools, monitor health and perform routine maintenance.",
      icon: <Wrench className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
  ];

  const nPlusSyllabus = [
    {
      title: "Networking fundamentals (LAN, WAN, MAN)",
      desc: "Understand topologies, transmission methods, and protocols.",
      icon: <Network className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
    {
      title: "IP addressing & subnetting",
      desc: "Master IPv4/IPv6 addressing, subnet masks, and calculations.",
      icon: <Code2 className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
    {
      title: "Switches, routers, and wireless networks",
      desc: "Configure wired/wireless devices and learn their functions.",
      icon: <Router className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
    {
      title: "Network troubleshooting & tools",
      desc: "Use ping, traceroute, Wireshark, and cable testers effectively.",
      icon: <Wrench className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
    {
      title: "Security concepts: firewalls, VPNs",
      desc: "Implement secure network designs and remote access.",
      icon: <Lock className="h-10 w-10 text-purple-600 animate-bounce" />,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 overflow-hidden">
      {/* Hero */}
      <section className="relative grid place-items-center overflow-hidden  text-white py-24">
        <img
          src={IMAGES.hero}
          alt="A+ N+"
          className="absolute inset-0 h-full w-full object-cover "
        />
        <div className="relative z-10 max-w-6xl px-6 text-center">
          <h1
            data-aos="fade-up"
            className="mt-6 text-4xl sm:text-5xl font-extrabold"
          >
            CompTIA A+ & N+ Certification Course
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            className="mt-4 mx-auto max-w-2xl text-white/90"
          >
            Build strong foundations in IT hardware, computer systems,
            networking, and security essentials with hands-on labs.
          </p>
          <div
            data-aos="fade-up"
            data-aos-delay="300"
            className="mt-8 flex items-center justify-center gap-4"
          >
            <Link
              to="/contact"
              className="rounded-2xl bg-white px-6 py-3 font-semibold text-purple-700 shadow hover:scale-[1.02] transition"
            >
              Enroll Now
            </Link>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="mx-auto max-w-6xl px-6 py-12 p-4">
        <div className="flex items-center justify-center gap-4 mb-8 ">
          <button
            onClick={() => setTab("a+")}
            className={`px-5 py-2 p-4 rounded-full font-medium transition ${
              tab === "a+"
                ? "bg-purple-600 text-white"
                : "bg-white shadow text-slate-700"
            }`}
          >
            A+ Hardware
          </button>
          <button
            onClick={() => setTab("n+")}
            className={`px-5 py-2 rounded-full font-medium transition ${
              tab === "n+"
                ? "bg-purple-600 text-white"
                : "bg-white shadow text-slate-700"
            }`}
          >
            N+ Networking
          </button>
        </div>

        {/* Curriculum Cards */}
        <div className="text-center" data-aos="fade-up">
          <h2 className="text-2xl font-bold">
            {tab === "a+" ? "A+ Curriculum" : "N+ Curriculum"}
          </h2>
          <p className="mt-2 text-slate-600">
            {tab === "a+"
              ? "Learn core hardware, troubleshooting and system support."
              : "Master networking concepts, protocols, and security."}
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-6">
            {(tab === "a+" ? aPlusSyllabus : nPlusSyllabus).map((item, i) => {
              const gradients = [
                "bg-gradient-to-r from-pink-400 to-pink-600",
                "bg-gradient-to-r from-purple-400 to-indigo-600",
                "bg-gradient-to-r from-blue-400 to-cyan-600",
                "bg-gradient-to-r from-green-400 to-lime-600",
                "bg-gradient-to-r from-yellow-400 to-orange-600",
                "bg-gradient-to-r from-teal-400 to-emerald-600",
              ];
              const gradient = gradients[i % gradients.length];

              return (
                <div
                  key={i}
                  className={`w-80 rounded-xl p-6 shadow-lg transition duration-300 hover:scale-105 hover:shadow-xl text-white ${gradient}`}
                  data-aos="flip-up"
                  data-aos-delay={i * 120}
                >
                  <div className="flex items-start gap-4 ">
                    <div
                      data-aos="zoom-in"
                      data-aos-delay={i * 150}
                      className="animate-bounce"
                    >
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">{item.title}</h4>
                      <p className="text-sm mt-1 opacity-90">{item.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 rounded-full bg-purple-600 px-5 py-3 text-white font-semibold hover:bg-purple-700 transition"
            >
              <span>Enroll for {tab === "a+" ? "A+" : "N+"}</span>
              <ChevronDown className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Practical Labs & Projects */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <div className="mt-16" data-aos="fade-up">
          <h3 className="text-xl font-bold text-center">
            Practical Labs & Projects
          </h3>
          <p className="mt-2 text-slate-600 text-center">
            Labs designed to reinforce hardware & networking concepts.
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-6">
            {commonProjects.map((p, i) => {
              const Icon = p.icon;

              const gradients = [
                "bg-gradient-to-r from-pink-400 to-pink-600",
                "bg-gradient-to-r from-purple-400 to-indigo-600",
                "bg-gradient-to-r from-blue-400 to-cyan-600",
                "bg-gradient-to-r from-green-400 to-lime-600",
                "bg-gradient-to-r from-yellow-400 to-orange-600",
                "bg-gradient-to-r from-teal-400 to-emerald-600",
              ];
              const gradient = gradients[i % gradients.length];

              return (
                <div
                  key={i}
                  className={`w-80 rounded-xl p-6 shadow-lg text-white transition duration-300 hover:scale-105 hover:shadow-xl ${gradient}`}
                  data-aos="fade-up"
                  data-aos-delay={i * 120}
                >
                  <div className="flex flex-col items-center text-center gap-4">
                    <Icon className="h-12 w-12 animate-bounce" />
                    <div>
                      <h4 className="font-semibold text-lg">{p.title}</h4>
                      <p className="text-sm opacity-90 mt-1">{p.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12">
        <div className="mx-auto max-w-6xl px-6">
          <h3 className="text-2xl font-bold mb-4">Frequently Asked</h3>
          <div className="mt-4 space-y-3">
            {[
              {
                q: "Do I need prior experience?",
                a: "No — we start with basics in hardware and networking and move to advanced concepts.",
              },
              {
                q: "Will I be job-ready after this course?",
                a: "Yes — A+ and N+ are industry-recognized certifications that prepare you for IT support and networking roles.",
              },
              {
                q: "Are certifications included in the course fee?",
                a: "Training is included, but exam vouchers for CompTIA A+ and N+ need to be purchased separately. Our counsellors will guide you through the process.",
              },
              {
                q: "What kind of practical labs will I get?",
                a: "You’ll work on real PC assembly, OS installation, network setup, troubleshooting simulations, and security configurations — just like in real IT support jobs.",
              },
              {
                q: "Can I pursue this course while studying/working?",
                a: "Yes — we offer flexible weekend and evening batches so you can balance it with your studies or job.",
              },
            ].map((faq, i) => (
              <details
                key={i}
                className="group rounded-lg border p-4 hover:bg-purple-50 hover:ring-purple-300 transition duration-300"
              >
                <summary className="flex cursor-pointer items-center justify-between font-medium">
                  {faq.q}{" "}
                  <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180 animate-pulse" />
                </summary>
                <p className="mt-2 text-sm text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <div
          className="rounded-3xl bg-purple-600 p-8 text-white shadow-lg"
          data-aos="zoom-in"
        >
          <div className="grid gap-6 md:grid-cols-2 items-center">
            <div>
              <h4 className="text-2xl font-bold">
                Ready to launch your IT career with A+ & N+?
              </h4>
              <p className="mt-2 text-purple-100">
                Learn hardware, troubleshooting, networking and security skills
                to stand out in the IT industry.
              </p>
            </div>
            <div className="flex items-center justify-end gap-4">
              <Link
                to="/contact"
                className="rounded-full bg-white px-6 py-3 text-purple-700 font-semibold"
              >
                Apply Now
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
